import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { of } from 'rxjs';
import { AuthService } from '@core/auth/services/auth.service';
import { UserPreferenceService } from '@core/services/user-preference/user-preference.service';
import { LoggedInUser } from '../models/user.model';
import { environment } from '@env/environment';
import { OrganizationService } from '@core/services/organization/organization.service';
import { BASE_ORG_TOKEN } from '../tokens/selected-org.token';

export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const userPreferenceService = inject(UserPreferenceService);
  const organizationService = inject(OrganizationService);
  const baseOrgSubject = inject(BASE_ORG_TOKEN);
  const authUrl = environment.authUrl;
  const requiredPermissions: string[] = route.data['requiredPermissions'] ?? [];

  if (state.url.startsWith('/auth-callback')) {
    return of(true);
  }

  return authService.checkAuthOnLoad().pipe(
    switchMap((isAuthenticated) => {
      if (!isAuthenticated) {
        return of(
          router.createUrlTree(['/signin'], {
            queryParams: { returnUrl: state.url },
          })
        );
      }

      // If already on select-organization or select-branch, allow
      if (
        state.url.includes('select-organization') ||
        state.url.includes('select-branch')
      ) {
        return of(true);
      }

      // Check if organization is selected (either in memory or cache)
      return userPreferenceService.loggedInUser$.pipe(
        filter((user): user is LoggedInUser => !!user),
        take(1),
        switchMap((user) => {
          // Allow navigation to organization-initialization regardless of org state
          if (state.url.includes('organization-initialization')) {
            return of(true);
          }

          return userPreferenceService.selectedOrg$.pipe(
            take(1),
            switchMap((selectedOrg) => {
              if (selectedOrg) {
                // Restore doorway baseOrg marker from localStorage on refresh
                if (!baseOrgSubject.value) {
                  const cachedBaseOrg = organizationService.getBaseOrg();
                  if (cachedBaseOrg) {
                    baseOrgSubject.next(cachedBaseOrg);
                  }
                }

                const cachedBranch = organizationService.getSelectedBranch(
                  selectedOrg.id
                );
                if (cachedBranch) {
                  userPreferenceService.setSelectedBranch(cachedBranch);
                  return of(true);
                }
                return of(router.createUrlTree(['/select-branch']));
              }

              const cachedOrg = organizationService.getSelectedOrganization();
              if (cachedOrg) {
                return organizationService
                  .getOrganizationById(cachedOrg.id)
                  .pipe(
                    map((org) => {
                      if (org) {
                        // Restore doorway baseOrg marker from localStorage on refresh
                        if (!baseOrgSubject.value) {
                          const cachedBaseOrg =
                            organizationService.getBaseOrg();
                          if (cachedBaseOrg) {
                            baseOrgSubject.next(cachedBaseOrg);
                          }
                        }

                        userPreferenceService.setSelectedOrg(cachedOrg);
                        const cachedBranch =
                          organizationService.getSelectedBranch(cachedOrg.id);
                        if (cachedBranch) {
                          userPreferenceService.setSelectedBranch(cachedBranch);
                          return true;
                        }
                        return router.createUrlTree(['/select-branch']);
                      } else {
                        return router.createUrlTree([
                          '/organization-initialization',
                          cachedOrg.id,
                        ]);
                      }
                    }),
                    catchError((err) => {
                      if (err.status === 404) {
                        return of(
                          router.createUrlTree([
                            '/organization-initialization',
                            cachedOrg.id,
                          ])
                        );
                      }
                      return of(false);
                    })
                  );
              }

              return of(router.createUrlTree(['/select-organization']));
            })
          );
        }),
        switchMap((canProceed) => {
          if (
            typeof canProceed !== 'boolean' ||
            !canProceed ||
            !requiredPermissions.length
          ) {
            return of(canProceed);
          }

          return userPreferenceService.loggedInUser$.pipe(
            filter(
              (user): user is LoggedInUser => !!user && !!user.permissions
            ),
            take(1),
            map((user) => {
              const hasPermission = requiredPermissions.some((p) =>
                (user.permissions || []).includes(p)
              );

              if (!hasPermission) {
                return router.createUrlTree(['/unauthorized']);
              }

              return true;
            })
          );
        })
      );
    })
  );
};
